import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { FiArrowRight } from 'react-icons/fi';
import PageShell from './components/PageShell';
import WorkflowStepper from './components/WorkflowStepper';
import { WORKFLOW_STEPS } from './config/workflowSteps';
import { hasPermission, getSubscriptionRole } from '../subscriptionAdmin/permissions';
import { adminGuideApi } from '../services/adminGuideApi';
import { mapApiStepToWorkflowStep } from '../AdminGuide/workflowStepMapper';
import FlowDiagram from '../AdminGuide/components/FlowDiagram';
import AlgorithmSteps from '../AdminGuide/components/AlgorithmSteps';

export default function WorkflowPage() {
  const navigate = useNavigate();
  const role = getSubscriptionRole();
  const [tab, setTab] = useState(0);
  const [apiSteps, setApiSteps] = useState(null);
  const [diagrams, setDiagrams] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let mounted = true;
    adminGuideApi
      .getWorkflowSteps()
      .then((data) => {
        if (!mounted) return;
        const steps = (data?.steps || []).map(mapApiStepToWorkflowStep);
        setApiSteps(steps);
        setDiagrams(data?.diagrams || []);
      })
      .catch(() => {
        if (mounted) setApiSteps(null);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const stepsSource = apiSteps?.length ? apiSteps : WORKFLOW_STEPS;

  const visible = useMemo(
    () => stepsSource.filter((s) => !s.permission || hasPermission(s.permission)),
    [stepsSource]
  );

  const paymentDiagram = diagrams.find((d) => d.content?.mermaid) || null;

  return (
    <PageShell
      title="Subscription workflow"
      subtitle="Follow steps 0–10 in order: customer → plan → contract → (offer) → payment → subscription → invoices → ops."
      backTo="/admin-subscriptions"
      showWorkflow={false}
    >
      {role && (
        <Chip
          label={`Role: ${role.replace('_', ' ')}`}
          size="small"
          sx={{ mb: 2, bgcolor: '#e0f2fe', color: '#0369a1' }}
        />
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Steps" />
        <Tab label="Flow diagrams" />
        <Tab label="Algorithms" />
      </Tabs>

      {tab === 0 && (
        <>
          <WorkflowStepper activeStep={-1} />
          <Grid container spacing={2}>
            {visible.map((step) => {
              const Icon = step.icon;
              return (
                <Grid item xs={12} sm={6} md={4} key={step.key}>
                  <Card
                    sx={{
                      height: '100%',
                      cursor: 'pointer',
                      transition: 'box-shadow 0.2s',
                      '&:hover': { boxShadow: 4 },
                    }}
                    onClick={() => navigate(step.path)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Chip label={`Step ${step.id}`} size="small" color="primary" variant="outlined" />
                        {step.externalCreatePath && (
                          <Chip label="Create on /create-user" size="small" variant="outlined" />
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                        <Icon size={22} color="#6366f1" />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {step.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {step.subtitle}
                      </Typography>
                      <Button
                        size="small"
                        endIcon={<FiArrowRight />}
                        sx={{ mt: 2 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(step.path);
                        }}
                      >
                        Open step
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      {tab === 1 && (
        <Box>
          {paymentDiagram ? (
            <FlowDiagram chart={paymentDiagram.content.mermaid} title={paymentDiagram.title} />
          ) : (
            <FlowDiagram
              chart={`stateDiagram-v2
  [*] --> draft
  draft --> pending_approval: submit
  pending_approval --> approved: approve
  approved --> pending_payment: send_for_payment
  pending_payment --> active: first_payment_captured
  active --> suspended: suspend
  suspended --> active: resume`}
              title="Contract lifecycle"
            />
          )}
          {!apiSteps && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Using local fallback diagram — run seed:admin-guide for full docs.
            </Typography>
          )}
        </Box>
      )}

      {tab === 2 && (
        <Box>
          {visible.map((step) => (
            <Box key={step.key} sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Step {step.id} — {step.title}
              </Typography>
              <AlgorithmSteps steps={step.algorithmSteps || []} />
            </Box>
          ))}
          {!visible.some((s) => s.algorithmSteps?.length) && (
            <Typography color="text.secondary">
              Algorithm steps load from the admin guide API. Run npm run seed:admin-guide on the backend.
            </Typography>
          )}
        </Box>
      )}
    </PageShell>
  );
}
